import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "../api/client";
import { useEvent } from "../context/EventProvider";

/**
 * THE SIGNED-IN PERSON'S PHOTO — and it is worth saying WHICH photo, because this
 * product has no "profile picture" at all.
 *
 *   GET /v2/domains/:d/faces?userId=…        api-v1
 *
 * ⚠️ WHAT IS BEING SHOWN IS A REFERENCE FACE. `UserFaces` holds the portraits attached
 * to somebody so that the album's recognition can find them in the event's pictures —
 * the back office adds them from a user's "Visages" tab. There is no column on `Users`
 * for an avatar, and no screen where a participant chooses one: this is the only photo
 * of a participant the product stores. It is a photo of them, so it stands in for their
 * initials; it is not something they picked, so nothing here insists on it.
 *
 * ⚠️ THE MOST RECENT ONE. The route sorts by id descending, and several faces can be
 * attached to one person over the years — the first is the one added last.
 *
 * ⚠️ THE URL IS SIGNED, SO IT EXPIRES. Nothing to renew here: whoever displays it falls
 * back to the initials if the image does not load, which is also what happens when the
 * account has no face at all.
 */
/**
 * `thumbUrl` : la vignette (800 px), signée par la route quand elle existe. C'est elle
 * qu'on affiche : un portrait se montre petit, partout, et l'original peut peser une
 * photo de téléphone entière (7,5 Mo, relevé le 2026-09-13). `fileUrl` reste le repli,
 * pour une API qui ne dirait pas encore la vignette.
 */
export type Face = {
  /** L'identifiant de la LIGNE `UserFaces` — celui que la suppression vise. */
  id?: number;
  fileUrl?: string | null;
  thumbUrl?: string | null;
};

/**
 * Les visages attachés à quelqu'un, du plus récent au plus ancien (la route trie).
 *
 * Séparée de `fetchPhoto` parce qu'effacer sa photo demande les IDENTIFIANTS, là où
 * l'afficher ne demande qu'une adresse (voir `album/face.ts`, `removeOwnPortrait`).
 */
export async function fetchFaces(
  domainId: number,
  userId: number,
  signal?: AbortSignal,
): Promise<ReadonlyArray<Face>> {
  const faces = await apiFetch<ReadonlyArray<Face> | null>(
    `/v2/domains/${domainId}/faces?userId=${userId}`,
    { signal },
  );
  return faces ?? [];
}

export async function fetchPhoto(
  domainId: number,
  userId: number,
  signal?: AbortSignal,
): Promise<string | null> {
  const faces = await fetchFaces(domainId, userId, signal);
  const url = faces
    .map((face) => (face?.thumbUrl ?? face?.fileUrl ?? "").trim())
    .find((candidate) => candidate !== "");
  return url ?? null;
}

/**
 * ⚠️ ASKED ONLY FOR THE SIGNED-IN PERSON'S OWN ID. The route takes a `userId` in its
 * query and checks nothing beyond the token, so passing anything else here would turn a
 * decoration into a way of reading other people's portraits.
 */
export function usePhoto(userId: number | undefined): string | null {
  const event = useEvent();

  const q = useQuery({
    queryKey: ["photo", event?.domainId, userId],
    queryFn: ({ signal }) => fetchPhoto(event!.domainId, userId!, signal),
    enabled: !!event && typeof userId === "number",
    // It does not change during a visit, and an account without a face is an answer,
    // not something to keep asking for.
    staleTime: Infinity,
    retry: false,
    retryOnMount: false,
  });

  return q.data ?? null;
}
